import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  CustomEventCollectionPageViewSettingsDto,
  SimpleCollection,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { AddAnotherButton } from "@src/components/Utils/AddAnotherButton";
import { CollectionListItem } from "@src/features/customEvents/components/subpages/CollectionListItem";
import { useCollections } from "@src/graphql/hooks/useCollections";
import {
  selectedCurrentCustomEvent,
  setCustomEventCollections,
} from "@src/store/slices/customEvents";

interface CollectionPageViewProps {
  isArchived: boolean;
}

export const CollectionPageView = ({ isArchived }: CollectionPageViewProps) => {
  const dispatch = useDispatch();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const collectionsData = useCollections();

  const collectionOptions =
    collectionsData?.collections?.edges?.map((edge) => edge.node) ?? [];

  const [selectedCollections, setSelectedCollections] = useImmer<
    (SimpleCollection | null)[]
  >([null]);
  const [initializedFromEvent, setInitializedFromEvent] = useImmer(false);

  // Handle initial collection selection when collectionOptions become available
  useEffect(() => {
    if (
      collectionOptions.length > 0 &&
      currentEvent?.type === "collectionPageView" &&
      !initializedFromEvent
    ) {
      const initialCollections = currentEvent.settings.map(
        (eventSetting) =>
          collectionOptions.find(
            (option) => option.handle === eventSetting.handle
          ) ?? null
      );
      setSelectedCollections(
        initialCollections.length > 0 ? initialCollections : [null]
      );
      setInitializedFromEvent(true);
    }
  }, [collectionOptions, currentEvent, initializedFromEvent]);

  const selectedCollectionsToSettingsDto = (
    selectedCollections: (SimpleCollection | null)[]
  ): CustomEventCollectionPageViewSettingsDto => {
    return selectedCollections
      .filter(
        (collection): collection is SimpleCollection => collection !== null
      )
      .map((collection) => ({ handle: collection.handle }));
  };

  const handleAddCollection = () => {
    setSelectedCollections((draft) => {
      draft.push(null); // placeholder for a new collection selection
    });
  };

  const handleCollectionChange = (
    index: number,
    newValue: SimpleCollection | null
  ) => {
    setSelectedCollections((draft) => {
      draft[index] = newValue;
    });
  };

  const handleRemoveCollection = (index: number) => {
    setSelectedCollections((draft) => {
      draft.splice(index, 1);
    });
  };

  useEffect(() => {
    dispatch(
      setCustomEventCollections({
        collections: selectedCollectionsToSettingsDto(selectedCollections),
      })
    );
  }, [selectedCollections]);

  if (!collectionsData?.collections?.edges?.length) {
    return <div>Loading collections...</div>;
  }

  return (
    <Stack width="100%" marginTop="15px">
      {selectedCollections.map((selectedCollection, index) => (
        <CollectionListItem
          key={index}
          selectedCollection={selectedCollection}
          selectedCollections={selectedCollections}
          options={collectionOptions}
          isArchived={isArchived}
          onChange={(newValue) => handleCollectionChange(index, newValue)}
          onRemove={() => handleRemoveCollection(index)}
        />
      ))}
      {!isArchived && (
        <AddAnotherButton
          onAddClick={handleAddCollection}
          itemLabel={"Add another collection"}
        />
      )}
    </Stack>
  );
};
